import { FILE_SIZE_LIMIT } from '@/src/utils/fileConsts';
import {
  downloadFile,
  FILE_FORMATS,
  transformBToMB,
} from '@/src/utils/fileUtil';
import { useMutation } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import MultipleFileInput from '../../../../components/inputs/StyledInputs/MultipleFileInput';
import { ContractDraftAttachment } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { fetchContractDraftAttachment } from '../../../../services/accounting/ACFormsService';

type ContractAttachmentsProps = {
  draftID?: string;
  draftAttachments: ContractDraftAttachment[];
  removeDraftAttachment: (uniqueName: string) => void;
};

const ContractAttachments = ({
  draftID,
  draftAttachments,
  removeDraftAttachment,
}: ContractAttachmentsProps) => {
  const { control } = useFormContext();
  const toast = useRef<Toast>(null);

  const attachmentRequest = useMutation({
    mutationFn: (attachment: ContractDraftAttachment) =>
      fetchContractDraftAttachment(draftID ?? '', attachment.uniqueName),
    onSuccess: (response, attachment) => {
      downloadFile(response, attachment.name);
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Contract Attachment',
        "The attachment couldn't be downloaded",
        3000
      );
    },
  });

  const storedSize = draftAttachments.reduce(
    (total, attachment) => total + attachment.size,
    0
  );
  const availableLimit = Math.max(FILE_SIZE_LIMIT - storedSize, 0);

  const isDownloading = (uniqueName: string) =>
    attachmentRequest.isPending &&
    attachmentRequest.variables?.uniqueName === uniqueName;

  return (
    <div>
      <ErrorToast toastRef={toast} />
      {!!draftAttachments.length && (
        <div className="mb-4">
          <div className="text-standard mb-2">
            The files saved on this draft are:
          </div>
          {draftAttachments.map((attachment) => (
            <span key={attachment.uniqueName} className="flex mb-1">
              {isDownloading(attachment.uniqueName) ? (
                <ProgressSpinner className="w-1rem h-auto ml-1" />
              ) : (
                <span
                  className="ml-1 mr-2 totalColor cursor-pointer hover:underline"
                  onClick={() => {
                    if (draftID) attachmentRequest.mutate(attachment);
                  }}
                >
                  {attachment.name}
                </span>
              )}
              <i
                onClick={() => removeDraftAttachment(attachment.uniqueName)}
                className="pi pi-ban errorText cursor-pointer my-auto"
              />
            </span>
          ))}
          <div className="mt-2 text-standard">
            To add more attachments select them below:
          </div>
        </div>
      )}
      <Controller
        name="attachments"
        aria-label="file-input"
        control={control}
        render={({ field: { onChange, value } }) => (
          <div>
            <MultipleFileInput
              value={value}
              onChange={onChange}
              acceptedFormats={FILE_FORMATS}
              totalLimit={availableLimit}
              totalLimitLabel={`${transformBToMB(availableLimit)} MB`}
              emptyMessage={`Optional: upload contract attachment files. Allowed extensions: ${FILE_FORMATS.join(
                ', '
              )}.`}
            />
          </div>
        )}
      />
    </div>
  );
};

export default ContractAttachments;
